import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Formik } from 'formik';
import * as Yup from 'yup';

import ScheduleReportModal from './ScheduleReportModal';

import { apiClient } from 'Helpers/network';

import Button from 'CommonComponents/Button';
import DatePickerField from 'CommonComponents/DatePickerField';
import Modal from 'CommonComponents/Modal';
import ModalFooter from 'CommonComponents/ModalFooter';
import ModalHeader from 'CommonComponents/ModalHeader';
import MultiSelectField from 'CommonComponents/MultiSelectField';
import SelectField from 'CommonComponents/SelectField';
import TextButton from 'CommonComponents/TextButton';

import { useConfirmationModal } from 'Hooks/useConfirmationModal';
import useModal from 'Hooks/useModal';
import { useToast } from 'Hooks/useToast';

import CircleInfoIcon from 'Assets/icons/circle-info.svg';
import DownloadIcon from 'Assets/icons/download.svg';
import EditIcon from 'Assets/icons/edit.svg';
import TrashIcon from 'Assets/icons/trash.svg';
import XMarkIcon from 'Assets/icons/xmark.svg';

/**
 * Generic parameter form for a centre-launched report, rendered inside the
 * shared Modal shell (driven by the parent's useModal hook). One control per
 * entry in the report's parameterSchema; select/multiselect options come from
 * the injected optionSources. Submitting POSTs { type, ...values } to storeUrl.
 *
 * On top of generation this form is the hub for saved filter sets: a grouped
 * "Saved filters" dropdown (personal sets + shared templates) loads a stored
 * parameter set into the form; own sets (and, for admins, shared templates) can
 * be edited or deleted; and the current parameters can be saved as a new set.
 * `prefill` (a row's stored options) powers the Re-run flow.
 *
 * Fields may opt in to inline `options`, options that depend on another field's
 * value (`dependsOn`, cleared when that value changes), a `hint`, a `placeholder`
 * (`:count` becomes the number of options) and an `emptyMessage` that replaces the
 * form when the field has nothing to offer. A report with an `onScreenUrl` also
 * gets a "View on screen" button that opens it with the form's values.
 */
export default function ReportParameterForm({
    modalId,
    modalRef,
    modalElement,
    report,
    optionSources = {},
    storeUrl,
    prefill = null,
    openedViaRerun = false,
    openNonce = 0,
    filterSetsIndexUrl,
    filterSetsStoreUrl,
    filterSetUpdateUrl,
    filterSetDestroyUrl,
    isAdmin = false,
    currentUserId = null,
    onSuccess,
    schedulesStoreUrl,
    schedulePreviewUrl,
    scheduleDefaultTimezone,
}) {
    const toast = useToast();
    const confirm = useConfirmationModal();
    const saveModal = useModal();
    const scheduleModal = useModal();

    // Snapshot of the current parameters passed to the schedule modal, reset on
    // every open so a stale snapshot never travels with a new schedule.
    const [scheduleSnapshot, setScheduleSnapshot] = useState({});
    const [scheduleNonce, setScheduleNonce] = useState(0);

    // Only centre-generated (configure-launched) reports can be scheduled;
    // context-launched reports run from their own page. Mirrors the store 422.
    const canSchedule = Boolean(report?.type) && report?.launchType === 'configure' && Boolean(schedulesStoreUrl);

    const openScheduleModal = (values) => {
        setScheduleSnapshot(values);
        setScheduleNonce((nonce) => nonce + 1);
        scheduleModal.show();
    };

    const schema = useMemo(() => report?.parameterSchema || [], [report]);

    // The source powering the Formik inputs: the Re-run prefill on open, or a
    // saved set's parameters once one is selected. enableReinitialize resets the
    // form whenever this changes.
    const [valuesSource, setValuesSource] = useState(prefill);
    const [selectedSetId, setSelectedSetId] = useState(null);
    const [bannerDismissed, setBannerDismissed] = useState(false);

    const [sets, setSets] = useState([]);
    const [setsLoading, setSetsLoading] = useState(false);

    const [saveMode, setSaveMode] = useState('create');
    const [saveSnapshot, setSaveSnapshot] = useState({});
    const [saveNonce, setSaveNonce] = useState(0);

    const loadSets = useCallback(
        (reportKey) => {
            if (!reportKey || !filterSetsIndexUrl) {
                return;
            }
            setSetsLoading(true);
            apiClient
                .get(filterSetsIndexUrl, { params: { report_key: reportKey } })
                .then((response) => setSets(response.data?.data || []))
                .catch(() => setSets([]))
                .finally(() => setSetsLoading(false));
        },
        [filterSetsIndexUrl],
    );

    // Fetch the report's saved filter sets when the form opens for a report.
    useEffect(() => {
        if (report?.type) {
            loadSets(report.type);
        }
    }, [report?.type, loadSets]);

    // Every time the form opens, start from the (re-run) prefill with nothing
    // selected. openNonce changes on every open so re-opening the same report
    // via Configure resets cleanly too.
    useEffect(() => {
        setValuesSource(prefill);
        setSelectedSetId(null);
        setBannerDismissed(false);
    }, [openNonce, prefill]);

    const initialValues = useMemo(() => buildInitialValues(schema, valuesSource, optionSources), [schema, valuesSource, optionSources]);
    const validationSchema = useMemo(() => buildValidationSchema(schema), [schema]);
    const orderedFields = useMemo(() => sortFieldsByType(schema), [schema]);

    const groupedSetOptions = useMemo(() => {
        const isMine = (set) => !set.is_shared && set.user_id === currentUserId;
        const isAnothers = (set) => !set.is_shared && set.user_id !== currentUserId;

        const mine = sets.filter(isMine).map((set) => ({ value: set.id, label: set.name }));
        // Only non-empty for report-centre admins — the backend only returns
        // other users' personal sets to them. Labelled with the owner's name so
        // it's never mistaken for the admin's own set.
        const others = sets.filter(isAnothers).map((set) => ({ value: set.id, label: set.owner_name ? `${set.name} (${set.owner_name})` : set.name }));
        const shared = sets.filter((set) => set.is_shared).map((set) => ({ value: set.id, label: set.name }));

        const groups = [];
        if (mine.length) {
            groups.push({ label: $t('My saved filters'), options: mine });
        }
        if (others.length) {
            groups.push({ label: $t("Other users' filters"), options: others });
        }
        if (shared.length) {
            groups.push({ label: $t('Shared templates'), options: shared });
        }
        return groups;
    }, [sets, currentUserId]);

    const selectedSet = sets.find((set) => set.id === selectedSetId) || null;
    // Shared templates are manageable by any report-centre admin. Personal sets
    // are owner-only to edit/delete — even for an admin viewing another user's
    // set — mirroring ReportFilterSetPolicy, which never lets an admin mutate
    // someone else's personal set.
    const canManageSelected = !!selectedSet && (selectedSet.is_shared ? isAdmin : selectedSet.user_id === currentUserId);

    const close = () => {
        modalElement && modalElement.hide();
    };

    const handleSelectSet = (option) => {
        if (!option) {
            setSelectedSetId(null);
            setValuesSource(prefill);
            return;
        }
        const set = sets.find((item) => item.id === option.value);
        setSelectedSetId(option.value);
        setValuesSource(set?.parameters || {});
    };

    const openSaveModal = (mode, values) => {
        setSaveMode(mode);
        setSaveSnapshot(values);
        setSaveNonce((nonce) => nonce + 1);
        saveModal.show();
    };

    const handleSaved = (set, mode) => {
        loadSets(report.type);
        if (mode === 'create' && set?.id) {
            setSelectedSetId(set.id);
            setValuesSource(set.parameters || saveSnapshot);
        }
        saveModal.hide();
    };

    const handleDeleteSet = () => {
        if (!selectedSet || !filterSetDestroyUrl) {
            return;
        }

        confirm({
            title: $t('Delete filter set?'),
            message: $t('This permanently removes this saved filter set. This cannot be undone.'),
            actions: [
                { confirmed: false, label: $t('Cancel'), variant: 'secondary', onClick: () => {} },
                {
                    confirmed: true,
                    label: $t('Delete permanently'),
                    variant: 'danger',
                    onClick: () =>
                        apiClient
                            .delete(filterSetDestroyUrl.replace('REPORT_FILTER_SET_ID', selectedSet.id))
                            .then(() => {
                                toast.success($t('Filter set deleted.'));
                                setSelectedSetId(null);
                                setValuesSource(prefill);
                                loadSets(report.type);
                            })
                            .catch(() => toast.error($t('Failed to delete the filter set. Please try again.'))),
                },
            ],
        });
    };

    const submit = (values, { setSubmitting, setErrors }) => {
        const payload = { type: report.type, ...values };
        if (selectedSetId) {
            payload.report_filter_set_id = selectedSetId;
        }

        return apiClient
            .post(storeUrl, payload)
            .then(() => {
                toast.success($t('Report generation started.'));
                close();
                onSuccess?.();
            })
            .catch((error) => {
                const serverErrors = error.response?.data?.errors;
                if (serverErrors) {
                    // Map a 422 payload onto the matching fields.
                    setErrors(Object.fromEntries(Object.entries(serverErrors).map(([field, messages]) => [field, [].concat(messages)[0]])));
                }
                toast.error(error.response?.data?.message || $t('Failed to generate the report. Please try again.'));
            })
            .finally(() => setSubmitting(false));
    };

    const optionsFor = (field, values) => fieldOptions(field, values, optionSources);

    // The first opted-in field with nothing to offer replaces the form with its message.
    const emptyField = (values) => schema.find((field) => field.emptyMessage && optionsFor(field, values).length === 0) || null;

    // Validated like Generate: an invalid form shows its errors instead of opening the report.
    const viewOnScreen = (values, validateForm, setTouched) =>
        validateForm().then((formErrors) => {
            if (Object.keys(formErrors).length > 0) {
                setTouched(Object.fromEntries(Object.keys(formErrors).map((key) => [key, true])));
                return;
            }
            window.location.href = withQuery(report.onScreenUrl, values);
        });

    return (
        <div id={modalId} ref={modalRef} tabIndex="-1" aria-hidden="true" className="hidden modal__box">
            {/* A report that is also viewed on screen has two more actions, so the modal is wider to keep them on one row. */}
            <div className={report?.onScreenUrl ? 'modal__container-large' : 'modal__container'}>
                <div className="modal__inner-container">
                    <ModalHeader title={report?.name} close={close}></ModalHeader>

                    <div className="modal__form-container">
                        <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={submit}>
                            {({
                                values,
                                errors,
                                touched,
                                setFieldValue,
                                setFieldTouched,
                                setTouched,
                                validateForm,
                                handleSubmit,
                                submitForm,
                                isSubmitting,
                            }) => {
                                const empty = emptyField(values);
                                const blocked = isSubmitting || empty !== null;

                                return (
                                    <form onSubmit={handleSubmit} className="tw-space-y-4">
                                        {groupedSetOptions.length > 0 && (
                                            <SavedFiltersBar
                                                options={groupedSetOptions}
                                                value={selectedSetId}
                                                loading={setsLoading}
                                                onSelect={handleSelectSet}
                                                canManage={canManageSelected}
                                                selectedSet={selectedSet}
                                                onEdit={() => openSaveModal('edit', values)}
                                                onDelete={handleDeleteSet}
                                            />
                                        )}

                                        {openedViaRerun && !bannerDismissed && (
                                            <ReRunBanner onSave={() => openSaveModal('create', values)} onDismiss={() => setBannerDismissed(true)} />
                                        )}

                                        {empty && (
                                            <div className="tw-p-6 tw-text-center tw-text-charcoal-75 tw-rounded-lg tw-bg-charcoal-10" role="status">
                                                {empty.emptyMessage}
                                            </div>
                                        )}

                                        {!empty &&
                                            orderedFields.map((field) => (
                                                <div key={field.key} className="tw-flex tw-flex-col tw-space-y-1.5">
                                                    {field.type !== 'checkbox' && (
                                                        <label htmlFor={`report-param-${field.key}`} className="text-input__label">
                                                            {field.label}
                                                            {field.required && <span className="tw-text-red tw-text-sm tw-ms-1">*</span>}
                                                        </label>
                                                    )}

                                                    <FieldControl
                                                        field={field}
                                                        value={values[field.key]}
                                                        options={optionsFor(field, values)}
                                                        onChange={(next) => {
                                                            setFieldValue(field.key, next);
                                                            schema
                                                                .filter((dependent) => dependent.dependsOn === field.key)
                                                                .forEach((dependent) =>
                                                                    setFieldValue(dependent.key, dependent.type === 'multiselect' ? [] : ''),
                                                                );
                                                        }}
                                                        onBlur={() => setFieldTouched(field.key, true)}
                                                    />

                                                    {field.hint && <p className="tw-m-0 tw-text-xs tw-italic tw-text-charcoal-75">{field.hint}</p>}

                                                    {touched[field.key] && errors[field.key] && (
                                                        <div className="tw-text-red tw-text-sm">{errors[field.key]}</div>
                                                    )}
                                                </div>
                                            ))}

                                        <div className="tw-flex tw-justify-end tw-gap-x-2 tw-pt-2">
                                            <Button type="button" variant="secondary" onClick={close} disabled={isSubmitting}>
                                                {$t('Cancel')}
                                            </Button>
                                            {/*
                                             * Submit via Formik's submitForm on click rather than a native
                                             * type="submit". A global jQuery handler in layouts.app disables
                                             * every button[type="submit"] on form submit to guard against
                                             * double submits; that never re-enables here because Formik
                                             * preventDefaults the submit (no page navigation), which would
                                             * leave this button stuck disabled outside React's control.
                                             */}
                                            <Button type="button" variant="secondary" onClick={() => openSaveModal('create', values)} disabled={blocked}>
                                                {$t('Save as filter set')}
                                            </Button>
                                            {canSchedule && (
                                                <Button type="button" variant="secondary" onClick={() => openScheduleModal(values)} disabled={blocked}>
                                                    {$t('Schedule this report')}
                                                </Button>
                                            )}
                                            {report?.onScreenUrl ? (
                                                <>
                                                    <Button
                                                        type="button"
                                                        variant="outline-neutral"
                                                        onClick={submitForm}
                                                        loading={isSubmitting}
                                                        disabled={blocked}
                                                        className="tw-flex tw-items-center tw-gap-1.5 tw-whitespace-nowrap"
                                                    >
                                                        <DownloadIcon className="tw-h-4 tw-w-4" aria-hidden="true" />
                                                        {$t('Generate Excel')}
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        variant="primary"
                                                        onClick={() => viewOnScreen(values, validateForm, setTouched)}
                                                        disabled={blocked}
                                                        className="tw-whitespace-nowrap"
                                                    >
                                                        {$t('View on screen')}
                                                    </Button>
                                                </>
                                            ) : (
                                                <Button type="button" variant="primary" onClick={submitForm} loading={isSubmitting} disabled={blocked}>
                                                    {$t('Generate')}
                                                </Button>
                                            )}
                                        </div>
                                    </form>
                                );
                            }}
                        </Formik>
                    </div>
                </div>
            </div>

            <SaveFilterSetModal
                modal={saveModal}
                mode={saveMode}
                nonce={saveNonce}
                report={report}
                values={saveSnapshot}
                schema={schema}
                optionSources={optionSources}
                isAdmin={isAdmin}
                existingSet={saveMode === 'edit' ? selectedSet : null}
                storeUrl={filterSetsStoreUrl}
                updateUrl={filterSetUpdateUrl}
                onSaved={handleSaved}
            />

            <ScheduleReportModal
                modal={scheduleModal}
                mode="create"
                nonce={scheduleNonce}
                report={report}
                parameters={scheduleSnapshot}
                defaultTimezone={scheduleDefaultTimezone}
                storeUrl={schedulesStoreUrl}
                previewUrl={schedulePreviewUrl}
                onSaved={() => scheduleModal.hide()}
            />
        </div>
    );
}

function SavedFiltersBar({ options, value, loading, onSelect, canManage, selectedSet, onEdit, onDelete }) {
    return (
        <div className="tw-pb-3 tw-border-b tw-border-grey tw-space-y-1.5">
            <div className="tw-flex tw-items-end tw-gap-2">
                <div className="tw-flex-1 tw-min-w-0">
                    <label className="text-input__label tw-block tw-mb-1">{$t('Saved filters')}</label>
                    <SelectField
                        value={value}
                        options={options}
                        isGrouped
                        clearable
                        isLoading={loading}
                        onChange={onSelect}
                        placeholder={$t('Saved filters')}
                    />
                </div>
                {canManage && (
                    <div className="tw-flex tw-flex-shrink-0 tw-gap-1">
                        <Button type="button" variant="secondary" small onClick={onEdit} className="tw-flex tw-items-center tw-gap-1">
                            <EditIcon className="tw-w-3.5 tw-h-3.5" />
                            {$t('Edit')}
                        </Button>
                        <Button type="button" variant="danger" small onClick={onDelete} className="tw-flex tw-items-center tw-gap-1">
                            <TrashIcon className="tw-w-3 tw-h-3" />
                            {$t('Delete')}
                        </Button>
                    </div>
                )}
            </div>
            {/* Surface the selected set's description (notably for shared templates). */}
            {selectedSet && selectedSet.description && <p className="tw-text-xs tw-text-charcoal-75 tw-m-0">{selectedSet.description}</p>}
        </div>
    );
}

function ReRunBanner({ onSave, onDismiss }) {
    return (
        <div className="tw-flex tw-items-center tw-gap-3 tw-p-3 tw-rounded-lg tw-bg-blue-50 tw-text-blue" role="status">
            <CircleInfoIcon className="tw-w-5 tw-h-5 tw-flex-shrink-0" />
            <div className="tw-flex-1 tw-text-sm">{$t('Save these parameters as a filter set to reuse them.')}</div>
            <TextButton variant="primary" onClick={onSave} className="tw-flex-shrink-0">
                {$t('Save as filter set')}
            </TextButton>
            <button
                type="button"
                onClick={onDismiss}
                title={$t('Dismiss')}
                aria-label={$t('Dismiss')}
                className="tw-flex-shrink-0 tw-text-blue hover:tw-text-dark-blue"
            >
                <XMarkIcon className="tw-w-4 tw-h-4" />
            </button>
        </div>
    );
}

/**
 * Create/edit a saved filter set. The name/description/share form is Formik-
 * driven; the parameters being saved are the snapshot of the parent form's
 * current values, shown read-only in a preview so the user knows exactly what
 * is being stored before publishing.
 */
function SaveFilterSetModal({ modal, mode, nonce, report, values, schema, optionSources, isAdmin, existingSet, storeUrl, updateUrl, onSaved }) {
    const toast = useToast();
    const isEdit = mode === 'edit';

    const close = () => modal.hide();

    const initialValues = useMemo(
        () => ({
            name: existingSet?.name || '',
            description: existingSet?.description || '',
            is_shared: existingSet?.is_shared || false,
        }),
        // nonce forces a reset on every open so a create form never keeps stale input.
        [existingSet, nonce],
    );

    const validationSchema = useMemo(
        () =>
            Yup.object().shape({
                name: Yup.string().required($t('This field is required')),
                description: Yup.string().nullable(),
                is_shared: Yup.boolean(),
            }),
        [],
    );

    const submit = (formValues, { setSubmitting, setErrors }) => {
        if (!report) {
            setSubmitting(false);
            return undefined;
        }

        const payload = {
            report_key: report.type,
            name: formValues.name,
            description: formValues.description || null,
            parameters: values || {},
            // Editing keeps the set's existing visibility; creating exposes the
            // "Share as template" choice to admins only, defaulting to personal.
            is_shared: isEdit ? !!existingSet?.is_shared : isAdmin && !!formValues.is_shared,
        };

        const promise = isEdit ? apiClient.put(updateUrl.replace('REPORT_FILTER_SET_ID', existingSet.id), payload) : apiClient.post(storeUrl, payload);

        return promise
            .then((response) => {
                toast.success(isEdit ? $t('Filter set updated.') : $t('Filter set saved.'));
                onSaved(response.data?.data || response.data, mode);
            })
            .catch((error) => {
                const serverErrors = error.response?.data?.errors;
                if (serverErrors) {
                    setErrors(Object.fromEntries(Object.entries(serverErrors).map(([field, messages]) => [field, [].concat(messages)[0]])));
                }
                toast.error(error.response?.data?.message || $t('Failed to save the filter set. Please try again.'));
            })
            .finally(() => setSubmitting(false));
    };

    return (
        <Modal modal={modal} modalIndexClass="tw-z-60">
            <ModalHeader title={isEdit ? $t('Edit filter set') : $t('Save filter set')} close={close}></ModalHeader>

            <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={submit}>
                {({ values: formValues, errors, touched, handleChange, handleBlur, setFieldValue, submitForm, isSubmitting }) => (
                    <div className="modal__form-container">
                        <div className="tw-flex tw-flex-col tw-space-y-1.5">
                            <label htmlFor="filter-set-name" className="text-input__label">
                                {$t('Name')}
                                <span className="tw-text-red tw-text-sm tw-ms-1">*</span>
                            </label>
                            <input
                                id="filter-set-name"
                                name="name"
                                type="text"
                                className="text-input tw-w-full"
                                value={formValues.name}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder={$t('Name')}
                            />
                            {touched.name && errors.name && <div className="tw-text-red tw-text-sm">{errors.name}</div>}
                        </div>

                        <div className="tw-flex tw-flex-col tw-space-y-1.5">
                            <label htmlFor="filter-set-description" className="text-input__label">
                                {$t('Description')}
                            </label>
                            <textarea
                                id="filter-set-description"
                                name="description"
                                rows={2}
                                className="text-input tw-w-full"
                                value={formValues.description}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder={$t('Description')}
                            />
                        </div>

                        {!isEdit && isAdmin && (
                            <div className="tw-flex tw-items-center tw-space-x-1.5">
                                <input
                                    id="filter-set-is-shared"
                                    type="checkbox"
                                    className="input-checkbox"
                                    checked={!!formValues.is_shared}
                                    onChange={(event) => setFieldValue('is_shared', event.target.checked)}
                                />
                                <label htmlFor="filter-set-is-shared" className="input-checkbox__label">
                                    {$t('Share as template')}
                                </label>
                            </div>
                        )}

                        <ParameterPreview schema={schema} values={values} optionSources={optionSources} />

                        <ModalFooter
                            submitButtonVariant="primary"
                            cancelButtonText={$t('Cancel')}
                            primaryButtonText={isEdit ? $t('Save changes') : $t('Save')}
                            onCancel={close}
                            handleSubmit={submitForm}
                            isSubmitting={isSubmitting}
                        />
                    </div>
                )}
            </Formik>
        </Modal>
    );
}

function ParameterPreview({ schema, values, optionSources }) {
    if (!schema.length) {
        return null;
    }

    return (
        <div className="tw-space-y-2 tw-p-3 tw-rounded-lg tw-bg-charcoal-10">
            <div className="tw-text-sm tw-font-semibold tw-text-charcoal">{$t('Parameters')}</div>
            <ul className="tw-space-y-1 tw-text-sm">
                {schema.map((field) => (
                    <li key={field.key} className="tw-flex tw-justify-between tw-gap-4">
                        <span className="tw-text-charcoal-75">{field.label}</span>
                        <span className="tw-text-charcoal tw-text-end tw-break-words">
                            {formatPreviewValue(field, (values || {})[field.key], optionSources, values)}
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function FieldControl({ field, value, options, onChange, onBlur }) {
    const placeholder = field.placeholder
        ? field.placeholder.replace(':count', String(options.flatMap((option) => option.options || [option]).length))
        : field.label;

    switch (field.type) {
        case 'select':
            return (
                <SelectField
                    value={value}
                    options={options}
                    isGrouped={!!field.grouped}
                    clearable
                    onChange={(option) => onChange(option ? option.value : '')}
                    placeholder={placeholder}
                />
            );
        case 'multiselect':
            return (
                <MultiSelectField
                    value={value || []}
                    options={options}
                    isGrouped={!!field.grouped}
                    showIndentation={!!field.grouped}
                    clearable
                    onChange={(selected) => onChange((selected || []).map((option) => option.value))}
                    placeholder={placeholder}
                />
            );
        case 'checkbox':
            return (
                <div className="tw-flex tw-items-center tw-space-x-1.5">
                    <input
                        id={`report-param-${field.key}`}
                        type="checkbox"
                        className="input-checkbox"
                        checked={!!value}
                        onChange={(event) => onChange(event.target.checked)}
                    />
                    <label htmlFor={`report-param-${field.key}`} className="input-checkbox__label">
                        {field.label}
                    </label>
                </div>
            );
        case 'date':
            return (
                <DatePickerField
                    name={field.key}
                    value={value || null}
                    valueFormat="yyyy-MM-dd"
                    className="text-input tw-w-full"
                    onChange={(name, next) => onChange(next)}
                    onCalendarClose={onBlur}
                />
            );
        default:
            return (
                <input
                    id={`report-param-${field.key}`}
                    type="text"
                    className="text-input tw-w-full"
                    value={value || ''}
                    onChange={(event) => onChange(event.target.value)}
                    onBlur={onBlur}
                    placeholder={field.label}
                />
            );
    }
}

/**
 * Display order for the parameter fields, grouped by control type:
 * select → multiselect → date → text, with checkboxes last. Array.sort is
 * stable, so fields of the same type keep their original schema order.
 */
const FIELD_TYPE_ORDER = ['select', 'multiselect', 'date', 'text', 'checkbox'];

function sortFieldsByType(schema) {
    const rank = (type) => {
        const index = FIELD_TYPE_ORDER.indexOf(type);

        return index === -1 ? FIELD_TYPE_ORDER.indexOf('text') : index;
    };

    const sorted = [...schema].sort((a, b) => rank(a.type) - rank(b.type));

    // A field whose options depend on another follows that field directly.
    schema
        .filter((field) => field.dependsOn)
        .forEach((field) => {
            const parentIndex = sorted.findIndex((candidate) => candidate.key === field.dependsOn);
            if (parentIndex === -1) {
                return;
            }
            sorted.splice(sorted.indexOf(field), 1);
            sorted.splice(sorted.findIndex((candidate) => candidate.key === field.dependsOn) + 1, 0, field);
        });

    return sorted;
}

/**
 * A field's options: its inline options, its option source, or for a field that
 * depends on another, the source's entry for that field's current value.
 */
function fieldOptions(field, values, optionSources) {
    if (field.options) {
        return field.options;
    }

    const source = optionSources[field.optionsSource];

    if (field.dependsOn) {
        const parent = (values || {})[field.dependsOn];

        return (source && parent !== '' && parent !== null && parent !== undefined && source[parent]) || [];
    }

    return source || [];
}

/** The empty value for a field's type. */
function emptyValue(field) {
    if (field.type === 'multiselect') {
        return [];
    }

    return field.type === 'checkbox' ? false : '';
}

/**
 * A stored value matched to the option it stands for, so a stored 12 selects the
 * option "12" and the other way round. Values with no matching option are kept.
 */
function alignToOptions(value, options) {
    const flat = options.flatMap((option) => (option.options ? option.options : [option]));
    const align = (item) => {
        const match = flat.find((option) => String(option.value) === String(item));

        return match ? match.value : item;
    };

    return Array.isArray(value) ? value.map(align) : align(value);
}

/** The URL with each non-empty value set as a query parameter, lists as key[]. */
function withQuery(url, values) {
    const target = new URL(url, window.location.origin);

    Object.entries(values).forEach(([key, value]) => {
        if (Array.isArray(value)) {
            value.forEach((item) => target.searchParams.append(`${key}[]`, item));
        } else if (value !== '' && value !== null && value !== undefined && value !== false) {
            target.searchParams.set(key, value);
        }
    });

    return target.toString();
}

/**
 * Initial Formik values from the schema, overridden by any values source (a
 * Re-run prefill or a selected saved set's parameters).
 */
function buildInitialValues(schema, valuesSource, optionSources = {}) {
    const values = {};
    schema.forEach((field) => {
        if (valuesSource && Object.prototype.hasOwnProperty.call(valuesSource, field.key)) {
            const stored = valuesSource[field.key];
            // A stored null is a cleared field: it stays cleared, as the type's empty value Yup accepts.
            values[field.key] = stored === null ? emptyValue(field) : alignToOptions(stored, fieldOptions(field, valuesSource, optionSources));
            return;
        }
        if (field.type === 'multiselect') {
            values[field.key] = [];
        } else if (field.type === 'checkbox') {
            values[field.key] = field.default ?? false;
        } else {
            values[field.key] = field.default ?? '';
        }
    });
    return values;
}

/**
 * A Yup schema built dynamically from the report's parameterSchema: multiselect
 * → array, checkbox → boolean, everything else → the appropriate primitive, with
 * `.required()` applied to required fields.
 */
function buildValidationSchema(schema) {
    const shape = {};
    const requiredMessage = $t('This field is required');

    schema.forEach((field) => {
        let rule;
        switch (field.type) {
            case 'multiselect':
                rule = Yup.array();
                if (field.required) {
                    rule = rule.min(1, requiredMessage);
                }
                break;
            case 'checkbox':
                rule = Yup.boolean();
                break;
            case 'date':
                rule = Yup.string().nullable();
                if (field.required) {
                    rule = rule.required(requiredMessage);
                }
                break;
            default:
                rule = Yup.string();
                if (field.required) {
                    rule = rule.required(requiredMessage);
                }
                break;
        }
        shape[field.key] = rule;
    });

    return Yup.object().shape(shape);
}

/**
 * Resolve a stored parameter value to a human-readable label for the save
 * preview: options are looked up (flattening grouped option sources), booleans
 * become Yes/No, and empty values render as an em dash.
 */
function formatPreviewValue(field, value, optionSources, values) {
    const options = fieldOptions(field, values, optionSources);

    switch (field.type) {
        case 'checkbox':
            return value ? $t('Yes') : $t('No');
        case 'multiselect': {
            const selected = value || [];
            if (!selected.length) {
                return '—';
            }
            return selected.map((item) => findOptionLabel(options, item)).join(', ');
        }
        case 'select':
            return value === '' || value === null || value === undefined ? '—' : findOptionLabel(options, value);
        default:
            return value === '' || value === null || value === undefined ? '—' : String(value);
    }
}

function findOptionLabel(options, value) {
    const flat = options.flatMap((option) => (option.options ? option.options : [option]));
    const found = flat.find((option) => String(option.value) === String(value));

    return found ? found.label : String(value);
}
